import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { Star, MapPin, MagnifyingGlass, FunnelSimple, ShieldCheck } from "@phosphor-icons/react";
import { useSEO } from "../lib/seo";

export default function BrowseWorkers() {
  const [workers, setWorkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "";
  const city = params.get("city") || "";

  useSEO({
    title: category ? `Browse ${category} Workers in Canada` : "Browse Trusted Workers Across Canada",
    description: "Find verified local skilled workers in Canada. Filter by service category, city, and rating. Real reviews from real customers.",
    path: `/workers${category ? `?category=${category}` : ""}`,
  });

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const qs = {};
    if (category) qs.category = category;
    if (city) qs.city = city;
    if (params.get("q")) qs.q = params.get("q");
    api.get("/workers", { params: qs })
      .then((r) => setWorkers(r.data))
      .finally(() => setLoading(false));
  }, [params, category, city]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    setParams(next);
  };

  const setCat = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("category", id); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Find a Pro</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-6">Browse Workers</h1>

        <form onSubmit={onSearch} className="bg-white border-2 border-slate-900 p-2 flex flex-col sm:flex-row gap-2 mb-6">
          <div className="flex items-center gap-2 px-3 flex-1">
            <MagnifyingGlass size={18} className="text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search workers, skills…"
              className="w-full py-2.5 text-base outline-none"
              data-testid="workers-search-input"
            />
          </div>
          <button type="submit" className="bg-brand text-white px-6 py-2.5 font-semibold" data-testid="workers-search-btn">
            Search
          </button>
        </form>

        <div className="flex flex-wrap gap-2 mb-8">
          <button onClick={() => setCat("")} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold border ${!category ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`} data-testid="filter-all">All</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold border ${category === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"}`} data-testid={`filter-${c.id}`}>
              {c.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-500">Loading…</div>
        ) : workers.length === 0 ? (
          <div className="border border-slate-200 p-12 text-center">
            <FunnelSimple size={32} className="mx-auto text-slate-400 mb-3" />
            <div className="font-display text-2xl mb-2">No workers yet</div>
            <p className="text-sm text-slate-600">Try a different category or search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workers.map((w) => (
              <Link key={w.user_id} to={`/workers/${w.user_id}`} className="border border-slate-200 p-6 bg-white hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`worker-card-${w.user_id}`}>
                <div className="flex items-start gap-3 mb-4">
                  {w.picture ? (
                    <img src={w.picture} alt={w.name} className="w-12 h-12 object-cover" />
                  ) : (
                    <div className="w-12 h-12 bg-brand text-white grid place-items-center font-display">{w.name?.[0]}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate">{w.name}</div>
                    <div className="text-xs text-slate-500 truncate">{w.headline}</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{w.category}</div>
                <p className="text-sm text-slate-700 line-clamp-2 mb-4">{w.bio}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-slate-700"><MapPin size={14} /> {w.city}, {w.province}</span>
                  <span className="flex items-center gap-1"><Star size={14} weight="fill" className="text-amber-500" /> {w.rating?.toFixed?.(1) || "—"} <span className="text-slate-500">({w.review_count})</span></span>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-sm font-semibold">${w.hourly_rate}/hr CAD</span>
                  <div className="flex items-center gap-1.5">
                    {w.verification?.status === "verified" && (
                      <span className="text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 flex items-center gap-1" data-testid={`verified-badge-${w.user_id}`}>
                        <ShieldCheck size={10} weight="fill" /> Verified
                      </span>
                    )}
                    {w.subscription_active && <span className="text-[10px] uppercase tracking-wider bg-brand text-white px-2 py-0.5">Pro</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
